import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { IconButton, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageContent } from "../components/PageContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { CatalogListName } from "../components/CatalogOriginChip";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useShowCatalogOriginMarkers } from "../hooks/useShowCatalogOriginMarkers";
import { CATALOG_NEW_ITEM_ID } from "./catalog/catalogNav";
import {
  useGetUnitsQuery,
  useDeleteUnitMutation,
} from "../query/unitsQuery";
import type { UnitId } from "@KassaroAB/shared/types/ids";

export const UnitsView = () => {
  const { t } = useTranslation();
  const { scope, pagePath } = useCatalogScope("units");
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);

  const [deleteId, setDeleteId] = useState<UnitId | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: units,
    isLoading,
    error: loadError,
  } = useGetUnitsQuery(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteUnitMutation(scope);

  const unitList = units ?? [];
  const deleteName = unitList.find((unit) => unit.id === deleteId)?.name ?? "";

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteMutation(deleteId, {
      onSuccess: () => {
        setSuccessMessage(t("unitsView.successDeleted"));
        setDeleteId(null);
      },
      onError: () => setSuccessMessage(null),
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isLoading && unitList.length === 0) {
    return (
      <PageContent title={t("unitsView.title")}>
        <LoadingContainer message={t("unitsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("unitsView.title")}
      actions={
        <RouterButtonLink to={`${pagePath}/${CATALOG_NEW_ITEM_ID}`}>
          <Plus size={16} aria-hidden />
          {t("unitsView.create")}
        </RouterButtonLink>
      }
    >
      {loadError && (
        <InfoBox variant="error">{t("unitsView.errorLoad")}</InfoBox>
      )}
      {deleteError && (
        <InfoBox variant="error">{t("unitsView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      {unitList.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("unitsView.tableName")}</TableHeaderCell>
                <TableHeaderCell>{t("unitsView.tableSuffix")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("unitsView.tableDecimals")}
                </TableHeaderCell>
                <TableHeaderCell align="right">{null}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {unitList.map((unit) => (
                <TableRow key={unit.id} to={`${pagePath}/${unit.id}`}>
                  <TableCell noWrap>
                    <CatalogListName
                      name={unit.name}
                      origin={unit.origin}
                      showOrigin={showCatalogOrigin}
                    />
                  </TableCell>
                  <TableCell noWrap>{unit.suffix}</TableCell>
                  <TableCell noWrap>{String(unit.decimals)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("common.delete")}
                      disabled={isDeletePending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteId(unit.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState
            message={`${t("unitsView.empty")} ${t("unitsView.emptyHint")}`}
          />
        </Card>
      )}
      <ConfirmDialog
        isOpen={deleteId != null}
        title={t("unitForm.deleteUnit")}
        message={t("unitsView.deleteConfirm", { name: deleteName })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContent>
  );
};
